import React, { useState } from 'react';
import { Skeleton, Tooltip, Menu, Dropdown, Modal } from 'antd';
import RedDot from '@assets/svgs/red-dot-icon.svg';
import MobileIcon from '@assets/svgs/blue-mobile-icon.svg';
import WinnerCup from '@assets/svgs/winner-cup.svg';
import RedPersonXIcon from '@assets/svgs/red-reject-applicant-icon.svg';
import EmailIcon from '@assets/svgs/blue-email-icon.svg';
import CommentIcon from '@assets/svgs/chat-balloon-icon.svg';
import LocationIcon from '@assets/svgs/location-icon.svg';
import ClockIcon from '@assets/svgs/clock-icon.svg';
import RedSettingsDots from '@assets/svgs/small-red-dots.svg';
import PinIcon from '@assets/svgs/purple-pin-icon.svg';
import EnvelopeIcon from '@assets/svgs/envelope-icon.svg';
import BlackEnvelopeIcon from '@assets/svgs/black-envelop-icon.svg';
import LeftArrow from '@assets/svgs/big-red-arrow-left.svg';
import UiTag from '@components/ui/tag/UiTag';
import LaptopIcon from '@assets/svgs/laptop-icon.svg';
import { useDrag } from 'react-dnd';
import StateEnum, { StateEnumTag } from '@constants/state.enum';
import { StepApplicantElement } from '@models/applicants';
import Applicant from '@components/app-components/applicant/Applicant';
import SendEmail from '@components/app-components/position/position-steps/applicant-card/send-email/SendEmail';
import { useRouter } from 'next/router';
import getApplicantComments from '@services/comments/comments-http.service';
import useNotification from '@hooks/notification/useNotification';
import { ApplicantCommentsModel } from '@models/comments';
import UiButton from '@components/ui/button/UiButton';
import { patchRejectApplicant, postHireApplicant } from '@services/applicants/applicants-http.service';
import { useTranslation } from 'next-i18next';
import { dateFromNow } from '@services/date/date-from-now';
import styles from './ApplicantCard.module.scss';
import AvatarIcon from '@assets/svgs/avatar-icon.svg';
import dayjs from 'dayjs';
import { getSentEmailTemplates } from '@services/email-templates/email-templates-http.service';
import { SentEmailApplicantElement } from '@models/email-templates';

export type PositionCardProps = {
  positionId: string;
  applicant: StepApplicantElement;
  applicants: Array<StepApplicantElement>;
  step: string;
  fetchRejectedCounter: () => void;
  fetchApplicants: () => void;
};

const ApplicantCard: React.FC<PositionCardProps> = ({
  positionId,
  applicants,
  applicant,
  fetchApplicants,
  step,
  fetchRejectedCounter,
}) => {
  const { t: translate } = useTranslation('applicantCard');
  const router = useRouter();
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [selectedApplicant, setSelectedApplicant] = useState<number>();
  const [comments, setComments] = useState<ApplicantCommentsModel>({ count: 0, items: [] });
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [openEmailTemplatesModal, setOpenEmailTemplatesModal] = useState<boolean>(false);
  const [showSendEmailForm, setShowSendEmailForm] = useState(false);
  const [showOnlyForm, setShowOnlyForm] = useState(false);
  const [sentEmails, setSentEmails] = useState<Array<SentEmailApplicantElement>>();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: 'card',
    item: { id: applicant.id, step: applicant.stepId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const handleClick = (event: any) => {
    setSelectedApplicant(event.currentTarget.id);
    setIsDrawerVisible(true);
  };

  const fetchApplicantComments = async () => {
    try {
      setCommentsLoading(true);
      const commentsElem: ApplicantCommentsModel = await getApplicantComments(applicant.id.toString());
      setComments(commentsElem);
      setCommentsLoading(false);
    } catch (e: any) {
      generalError();
      setCommentsLoading(false);
    }
  };

  const getEmailTemplates = async () => {
    try {
      const sentEmailsElem: Array<SentEmailApplicantElement> = await getSentEmailTemplates(
        applicant.id.toString(),
        positionId,
      );
      setSentEmails(sentEmailsElem);
    } catch (e: any) {
      generalError();
    }
    setOpenEmailTemplatesModal(true);
  };

  const openEmailTemplates = async () => {
    setOpenEmailTemplatesModal(true);
    await getEmailTemplates();
  };

  const renderComments = () => {
    if (commentsLoading) {
      return <Skeleton active />;
    }
    return (
      <div className={styles.wrapCommentsTooltip}>
        <div className={`h6 ${styles.titleComments}`}>{translate('comments')}</div>
        <div className={styles.wrapComments}>
          {comments.count > 0 ? (
            comments.items.map((comment) => {
              return (
                <div key={comment.id} className={styles.wrapTooltipComment}>
                  <div
                    className={`h6 ${styles.userComment}`}
                  >{`${comment.user.firstname} ${comment.user.lastname}`}</div>
                  <div className={`main-body-text ${styles.commentText}`}>{comment.comment}</div>
                </div>
              );
            })
          ) : (
            <div className={styles.wrapTooltipComment}>
              <div className={`h6 ${styles.userComment}`}>{translate('no_comments')}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const rejectApplicant = async () => {
    try {
      await patchRejectApplicant(positionId, applicant.id.toString(), applicant.stepId, true);
      await fetchApplicants();
      await fetchRejectedCounter();
    } catch (e: any) {
      generalError();
    }
  };

  const hireApplicant = async () => {
    try {
      await postHireApplicant(positionId, applicant.id.toString());
      await fetchApplicants();
    } catch (e: any) {
      generalError();
    }
  };

  const menuItems = [
    {
      key: 'reject-applicant',
      icon: <RedPersonXIcon />,
      onClick: (e: any) => {
        e.domEvent.preventDefault();
        e.domEvent.stopPropagation();
        rejectApplicant();
      },
      label: translate('rejection'),
      className: `main-body-text ${styles.rejectMenuItem} ${styles.applicantCardMenuItem}`,
    },
    {
      key: 'hire-applicant',
      icon: <WinnerCup />,
      onClick: (e: any) => {
        e.domEvent.preventDefault();
        e.domEvent.stopPropagation();
        hireApplicant();
      },
      label: translate('hire'),
      className: `main-body-text ${styles.applicantCardMenuItem}`,
    },
    {
      key: 'send-email',
      icon: <BlackEnvelopeIcon />,
      onClick: (e: any) => {
        e.domEvent.preventDefault();
        e.domEvent.stopPropagation();
        setShowOnlyForm(true);
        setOpenEmailTemplatesModal(true);
      },
      label: translate('send_email'),
      className: `main-body-text ${styles.applicantCardMenuItem}`,
    },
  ];

  const applicantCardDotsMenu = <Menu mode="vertical" items={menuItems} />;

  const templateModalTitle = (
    <div className={styles.modalTitle}>
      {!showOnlyForm && showSendEmailForm && (
        <span className={styles.titleLeftArrow} onClick={() => setShowSendEmailForm(false)}>
          <LeftArrow />
        </span>
      )}
      <div className={styles.modalTitleText}>
        {showOnlyForm ? translate('send_email') : translate('sent_emails_modal_title')}
      </div>
      {!showOnlyForm && !showSendEmailForm && (
        <UiButton
          className={styles.createButton}
          size="small"
          type="primary"
          icon={<EnvelopeIcon className={styles.envelopeIcon} />}
          onClick={() => setShowSendEmailForm(true)}
        >
          {translate('send_email')}
        </UiButton>
      )}
    </div>
  );

  // @ts-ignore
  const EmailCard = ({ date, stepName, subject, firstName, lastName }) => (
    <>
      <div data-testid="email-card" className={styles.wrapEmailSentCard}>
        <div className={styles.wrapUserIcon}>
          {lastName ? <AvatarIcon style={{ height: '32px' }} /> : <LaptopIcon style={{ 'margin-top': '3px' }} />}
        </div>
        <div className={styles.wrapEmailCardContent}>
          <div className={styles.wrapEmailTitle}>
            <span className={`h5 ${styles.templateTitle}`}>
              {lastName ? `${firstName} ${lastName} ` : translate('system_email')}
            </span>
            <span className={`small-caps-regular ${styles.templateDate}`}>{dayjs(date).format('DD MMM YYYY')}</span>
          </div>
          <div>
            <span className={styles.templateTitleField}>{translate('step')}</span>
            <span className={styles.templateValueField}>{stepName ? stepName : '-'}</span>
          </div>
          <div className={styles.separator}></div>
          <div>
            <span className={styles.templateTitleField}>{translate('subject_email')}</span>
            <span className={styles.templateValueField}>{subject}</span>
          </div>
        </div>
      </div>
    </>
  );

  const emailCards = () => {
    return (
      <div className={sentEmails && sentEmails?.length > 0 ? styles.emailCards : styles.emptyEmailCardsState}>
        {sentEmails && sentEmails?.length > 0 ? (
          sentEmails.map((email: any) => {
            return (
              <EmailCard
                key={email.id}
                date={email.createdDate}
                stepName={email.stepName}
                subject={email.subject}
                firstName={email.firstName}
                lastName={email.lastName}
              />
            );
          })
        ) : (
          <div className={styles.noEmailCards}>{translate('no_template_emails')}</div>
        )}
      </div>
    );
  };

  return (
    <div ref={dragPreview} className={styles.wrapPreviewCard}>
      {isDragging ? (
        <div />
      ) : (
        <div
          id={applicant.id.toString()}
          className={styles.wrapPositionCard}
          ref={drag}
          onClick={handleClick}
          role="button"
          onKeyDown={handleClick}
          tabIndex={0}
          data-testid="applicant-card"
        >
          {!applicant.viewed && (
            <Tooltip
              placement="top"
              mouseLeaveDelay={0.1}
              color="#3e4b5e"
              title={<div className={`small-body-text ${styles.redDotTooltip}`}>Δεν έχει προβληθεί</div>}
              trigger="hover"
            >
              <RedDot className={styles.redDotIcon} />
            </Tooltip>
          )}
          <div className={styles.positionCard}>
            <div className={styles.wrapCardHeader}>
              <div className={`h5 ${styles.nameText}`}>
                {applicant.firstName} {applicant.lastName}
              </div>
              <div className={styles.wrapEditDots}>
                <Dropdown dropdownRender={() => applicantCardDotsMenu} trigger={['click']} placement="bottomRight">
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    role="presentation"
                  >
                    <UiButton type="ghost" icon={<RedSettingsDots />} />
                  </a>
                </Dropdown>
              </div>
            </div>
            {applicant.location && (
              <div className={styles.locationTag}>
                <UiTag text={applicant.location} type={StateEnumTag.DEFAULT} icon={<LocationIcon />} />
              </div>
            )}
            <div className={styles.wrapPhoneEmail}>
              {applicant.phoneNumber && (
                <div
                  className={styles.wrapElementTag}
                  onClick={(e) => {
                    router.push(`tel:${applicant.phoneNumber}`);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    router.push(`tel:${applicant.phoneNumber}`);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <div className={styles.elementTag}>
                    <MobileIcon />
                    <div className="h6">{applicant.phoneNumber}</div>
                  </div>
                </div>
              )}
              {applicant.email && (
                <Tooltip
                  placement="topLeft"
                  color="#3e4b5e"
                  title={<div className="small-body-text">{translate('send_email')}</div>}
                  trigger="hover"
                  mouseLeaveDelay={0.1}
                >
                  <div
                    className={styles.wrapElementTag}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOnlyForm(true);
                      setOpenEmailTemplatesModal(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOnlyForm(true);
                      setOpenEmailTemplatesModal(true);
                    }}
                    data-testid="email-copy-button"
                  >
                    <div className={styles.elementTag}>
                      <EmailIcon />
                      <div className="h6">{applicant.email}</div>
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <div className={styles.wrapCardFooter}>
            <div className={styles.leftFooter}>
              <div className={`small-caps-regular ${styles.dateBefore}`}>
                <ClockIcon className={styles.clockIcon} />
                {dateFromNow(applicant.createdDate)}
              </div>
              {applicant.sourced && (
                <Tooltip
                  placement="top"
                  mouseLeaveDelay={0.1}
                  color="#3e4b5e"
                  title={<div className="small-body-text">Assigned</div>}
                  trigger="hover"
                >
                  <div className={styles.pinContainer}>
                    <PinIcon className={styles.pinIcon} data-testid="pin-icon" />
                  </div>
                </Tooltip>
              )}
            </div>
            <div className={styles.rightFooter}>
              <Tooltip
                placement="top"
                mouseLeaveDelay={0.1}
                color="#111725"
                title={translate('sent_emails')}
                trigger="hover"
                showArrow
              >
                <div
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await openEmailTemplates();
                  }}
                  className={styles.greyTags}
                  data-testid="email-modal-button"
                >
                  <EnvelopeIcon className={styles.envelope} />
                  <span
                    className={`small-caps-regular ${
                      applicant.emailCounter > 0 ? styles.hasCounterOnTag : styles.noCounterOnTag
                    }`}
                  >
                    <span data-testid="email-count">{applicant.emailCounter}</span>
                  </span>
                </div>
              </Tooltip>
              <Tooltip
                placement="bottom"
                mouseLeaveDelay={0.1}
                color="#111725"
                title={renderComments()}
                trigger="click"
                overlayStyle={{ backgroundColor: '#111725', background: '#111725', borderRadius: '8px' }}
                overlayClassName="commentsTooltip"
                showArrow
              >
                <div
                  role="button"
                  className={styles.greyTags}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await fetchApplicantComments();
                  }}
                  tabIndex={0}
                  onKeyDown={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await fetchApplicantComments();
                  }}
                  data-testid="comments-tooltip"
                >
                  <CommentIcon />
                  <div
                    className={`small-caps-regular ${
                      applicant.commentsCounter > 0 ? styles.hasCounterOnTag : styles.noCounterOnTag
                    }`}
                  >
                    {applicant.commentsCounter}
                  </div>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
      {selectedApplicant && (
        <Applicant
          key={selectedApplicant}
          applicants={applicants}
          setVisible={setIsDrawerVisible}
          visible={isDrawerVisible}
          applicantId={selectedApplicant}
          fetchApplicants={() => fetchApplicants()}
          arrows
        />
      )}
      <Modal
        title={templateModalTitle}
        open={openEmailTemplatesModal}
        footer={null}
        width={575}
        onCancel={() => {
          setOpenEmailTemplatesModal(false);
          setShowSendEmailForm(false);
          setShowOnlyForm(false);
        }}
      >
        {showOnlyForm || showSendEmailForm ? (
          <SendEmail
            applicant={applicant}
            step={step}
            positionId={positionId}
            setOpenEmailTemplatesModal={setOpenEmailTemplatesModal}
            setShowSendEmailForm={setShowSendEmailForm}
            fetchApplicants={() => fetchApplicants()}
          />
        ) : (
          emailCards()
        )}
      </Modal>
    </div>
  );
};

export default ApplicantCard;
